import { QUESTION_TYPE } from '../../fetchSurvey/constants/questionTypes';
import type { McqQuestion } from '../../fetchSurvey/types/mcq';
import type { RankOrderQuestion } from '../../fetchSurvey/types/rankOrder';
import type { SurveyQuestion } from '../../fetchSurvey/types';
import type { ScaleColumn, StatementRow } from '../../fetchSurvey/types/shared';

export function isMatrixType(type: SurveyQuestion['type']): boolean {
  return (
    type === QUESTION_TYPE.CFM_MATRIX ||
    type === QUESTION_TYPE.CSAT_MATRIX ||
    type === QUESTION_TYPE.RATING_MATRIX ||
    type === QUESTION_TYPE.SLIDER_MATRIX
  );
}

export function hasScaleColumnAnswerLogic(type: SurveyQuestion['type']): boolean {
  return (
    type === QUESTION_TYPE.CFM_MATRIX ||
    type === QUESTION_TYPE.CSAT_MATRIX ||
    type === QUESTION_TYPE.RATING_MATRIX
  );
}

export function getStatementRows(question: SurveyQuestion): StatementRow[] {
  if (!isMatrixType(question.type)) {
    return [];
  }

  const matrixQuestion = question as SurveyQuestion & { statementRows?: StatementRow[] };
  return matrixQuestion.statementRows ?? [];
}

export function getScaleColumns(question: SurveyQuestion): ScaleColumn[] {
  if (!hasScaleColumnAnswerLogic(question.type)) {
    return [];
  }

  const matrixQuestion = question as SurveyQuestion & { scaleColumns?: ScaleColumn[] };
  return matrixQuestion.scaleColumns ?? [];
}

export function hasRowAnswerLogic(question: SurveyQuestion): boolean {
  return getStatementRows(question).some(row => row.answerLogic !== undefined);
}

export function hasOptionAnswerLogic(question: SurveyQuestion): boolean {
  if (question.type === QUESTION_TYPE.MCQ) {
    return (question as McqQuestion).options.some(option => option.answerLogic !== undefined);
  }

  if (question.type === QUESTION_TYPE.RANK_ORDER) {
    return (question as RankOrderQuestion).options.some(option => option.answerLogic !== undefined);
  }

  return false;
}

export function hasScaleColumnAnswerLogicOnQuestion(question: SurveyQuestion): boolean {
  return getScaleColumns(question).some(column => column.answerLogic !== undefined);
}

export function hasAnswerLogic(question: SurveyQuestion): boolean {
  return (
    hasRowAnswerLogic(question) ||
    hasOptionAnswerLogic(question) ||
    hasScaleColumnAnswerLogicOnQuestion(question)
  );
}
